'use strict';

const libPictProvider = require('pict-provider');

// A project can carry a lot of line items; cap the loaded grid and warn at the ceiling
// (the per-record BidItem picker on the Line Items list handles the long tail).
const MAX_ROWS = 1500;
// Above this many rows we fall back from per-row Pict-Section-Pickers to native <select>s — creating
// several hundred picker views at once is heavy; native dropdowns keep a huge project responsive.
const PICKER_CAP = 400;

/**
 * LineItemBidItemMap — the "Bid Item Mapping" tab body on the Project workspace. Given a project, it
 * loads the project's Line Items and Bid Items, renders one row per line item with a **searchable
 * Bid Item picker (Pict-Section-Picker)** scoped to that project's bid items, and bulk-saves the changed
 * `LineItem.IDBidItem` FKs. Line items are project-scoped, so this mapping lives on the Project workspace
 * (it was previously on the Contract workspace, contract-anchored across the contract's projects).
 *
 * Renders into a host element owned by ProjectWorkspace (mirrors how the Leaflet map + association editors
 * mount from the workspace's OnEnter hook). No view of its own — HTML is hand-assigned into the mount, then
 * a Pict-Section-Picker is mounted per row (each fed a static, in-memory Options list + an OnChange hook).
 */
class LineItemBidItemMapProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;

		this._IDProject = 0;
		this._Mount = '';
		this._loading = false;
		this._loaded = false;
		this._saving = false;
		this._truncated = false;
		this._usePickers = true;
		this._message = '';
		this._messageClass = '';

		this._lineItems = [];
		this._bidItemsByProject = {};   // IDProject(string) -> [ bidItem, … ]
		this._bidItemLabelByID = {};    // IDBidItem(string) -> label
		this._pending = {};             // IDLineItem(string) -> new IDBidItem (number)

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('LineItemBidItemMap-CSS', _CSS, 520);
		}
	}

	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	get effectivePermissions() { return this.pict.EffectivePermissionsService; }
	get pickerProvider() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }

	_canEdit()
	{
		try { return this.effectivePermissions ? this.effectivePermissions.can('LineItem', 'Update') : true; }
		catch (pError) { return true; }
	}

	/**
	 * Render (and, on a project change, load) the mapping grid into a host element.
	 * Safe to call on every ProjectWorkspace render — reloads only when the project changes.
	 * @param {number|string} pIDProject
	 * @param {string} pMountSelector
	 */
	async openInto(pIDProject, pMountSelector)
	{
		const tmpID = parseInt(pIDProject, 10);
		this._Mount = pMountSelector;
		if (!tmpID) { return; }
		// AppData store the per-row pickers read/write their value through (one address per line item).
		this.pict.AppData.LineItemBidItemMap = this.pict.AppData.LineItemBidItemMap || {};
		this.pict.AppData.LineItemBidItemMap.RowValues = this.pict.AppData.LineItemBidItemMap.RowValues || {};

		if (tmpID === this._IDProject && (this._loaded || this._loading))
		{
			// Same project, host was just re-created by a workspace re-render — repaint from cache.
			this._render();
			return;
		}
		this._IDProject = tmpID;
		this._pending = {};
		this._loaded = false;
		this._loading = true;
		this._message = '';
		this._render();
		try { await this._loadAsync(tmpID); }
		catch (pError) { this.pict.log.warn(`LineItemBidItemMap load failed: ${pError && pError.message ? pError.message : pError}`); }
		this._loading = false;
		this._loaded = true;
		this._render();
	}

	async _loadAsync(pIDProject)
	{
		const tmpLineItems = await this._getJSON(`LineItems/FilteredTo/FBV~IDProject~EQ~${pIDProject}~FBV~Deleted~EQ~0~FSF~ItemNumber~ASC~0/0/${MAX_ROWS}`);
		const tmpBidItems = await this._getJSON(`BidItems/FilteredTo/FBV~IDProject~EQ~${pIDProject}~FBV~Deleted~EQ~0/0/${MAX_ROWS}`);

		this._lineItems = Array.isArray(tmpLineItems) ? tmpLineItems : [];
		this._truncated = (this._lineItems.length >= MAX_ROWS);
		// Searchable pickers for the common case; native dropdowns keep a very large project responsive.
		this._usePickers = this._canEdit() && (this._lineItems.length <= PICKER_CAP);

		this._bidItemsByProject = {};
		this._bidItemLabelByID = {};
		(Array.isArray(tmpBidItems) ? tmpBidItems : []).forEach((pBidItem) =>
		{
			const tmpProjectKey = String(pBidItem.IDProject || 0);
			if (!this._bidItemsByProject[tmpProjectKey]) { this._bidItemsByProject[tmpProjectKey] = []; }
			this._bidItemsByProject[tmpProjectKey].push(pBidItem);
			this._bidItemLabelByID[String(pBidItem.IDBidItem)] = this._bidItemLabel(pBidItem);
		});
	}

	_bidItemLabel(pBidItem)
	{
		const tmpNumber = pBidItem.ItemNumber || pBidItem.ItemCode || '';
		const tmpName = pBidItem.Name || `Bid Item ${pBidItem.IDBidItem}`;
		return (tmpNumber ? `${tmpNumber} — ` : '') + tmpName;
	}

	/** The current value to display for a row — a pending pick if there is one, else the stored FK. */
	_currentValue(pLineItem)
	{
		const tmpPending = this._pending[String(pLineItem.IDLineItem)];
		return (tmpPending !== undefined) ? tmpPending : (parseInt(pLineItem.IDBidItem, 10) || 0);
	}

	/** `[{ Value, Text }]` option list for a row: none + the row's project bid items (+ its current FK if off-project). */
	_pickerOptions(pLineItem)
	{
		const tmpProjectKey = String(pLineItem.IDProject || 0);
		const tmpCurrent = parseInt(pLineItem.IDBidItem, 10) || 0;
		const tmpOptions = [ { Value: 0, Text: '— none —' } ];
		let tmpHasCurrent = (tmpCurrent === 0);
		(this._bidItemsByProject[tmpProjectKey] || []).forEach((pBidItem) =>
		{
			const tmpID = parseInt(pBidItem.IDBidItem, 10);
			if (tmpID === tmpCurrent) { tmpHasCurrent = true; }
			tmpOptions.push({ Value: tmpID, Text: this._bidItemLabel(pBidItem) });
		});
		// Never silently drop an existing mapping that points at a bid item from another project.
		if (!tmpHasCurrent && tmpCurrent > 0)
		{
			tmpOptions.push({ Value: tmpCurrent, Text: `${this._bidItemLabelByID[String(tmpCurrent)] || `Bid Item ${tmpCurrent}`} (other project)` });
		}
		return tmpOptions;
	}

	/** Selection handler — used by both the per-row picker (OnChange) and the native fallback (onchange). */
	onPick(pIDLineItem, pValue)
	{
		const tmpIDLineItem = parseInt(pIDLineItem, 10);
		const tmpValue = parseInt(pValue, 10) || 0;
		const tmpLineItem = this._lineItems.find((pRow) => pRow.IDLineItem === tmpIDLineItem);
		const tmpOriginal = tmpLineItem ? (parseInt(tmpLineItem.IDBidItem, 10) || 0) : 0;
		if (tmpValue === tmpOriginal) { delete this._pending[String(tmpIDLineItem)]; }
		else { this._pending[String(tmpIDLineItem)] = tmpValue; }
		this._syncSaveBar();
		this._highlightRow(tmpIDLineItem);
	}

	_highlightRow(pIDLineItem)
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !this._Mount) { return; }
		const tmpEl = tmpCA.getElement(`${this._Mount} tr[data-li="${pIDLineItem}"]`);
		const tmpRow = (tmpEl && tmpEl.length) ? tmpEl[0] : null;
		if (tmpRow && tmpRow.classList) { tmpRow.classList.toggle('libim-changed', this._pending[String(pIDLineItem)] !== undefined); }
	}

	pendingCount() { return Object.keys(this._pending).length; }

	async saveAsync()
	{
		if (!this._canEdit()) { this._flash('You do not have permission to edit line items.', 'is-danger'); return; }
		const tmpKeys = Object.keys(this._pending);
		if (tmpKeys.length === 0 || this._saving) { return; }

		this._saving = true;
		this._syncSaveBar();

		let tmpOK = 0;
		let tmpFail = 0;
		for (let i = 0; i < tmpKeys.length; i++)
		{
			const tmpIDLineItem = parseInt(tmpKeys[i], 10);
			const tmpIDBidItem = this._pending[tmpKeys[i]] || 0;
			// Field-wise Meadow update — send only the id + the changed FK (never IDCustomer; the server owns it).
			const tmpRes = await this._putJSON('LineItem', { IDLineItem: tmpIDLineItem, IDBidItem: tmpIDBidItem });
			if (tmpRes && !tmpRes.error)
			{
				tmpOK++;
				const tmpLineItem = this._lineItems.find((pRow) => pRow.IDLineItem === tmpIDLineItem);
				if (tmpLineItem) { tmpLineItem.IDBidItem = tmpIDBidItem; }
				delete this._pending[tmpKeys[i]];
			}
			else { tmpFail++; }
		}

		this._saving = false;
		this._flash(`Saved ${tmpOK} mapping${tmpOK === 1 ? '' : 's'}${tmpFail ? `, ${tmpFail} failed` : ''}.`, tmpFail ? 'is-warning' : 'is-success');
		this._render();
	}

	_flash(pMessage, pClass)
	{
		this._message = pMessage;
		this._messageClass = pClass || 'is-info';
	}

	// Cheap partial repaint of just the save bar (avoids repainting the whole grid on every pick).
	_syncSaveBar()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !this._Mount) { return; }
		if (tmpCA.getElement(`${this._Mount} .libim-savebar`).length === 0) { return; }
		tmpCA.assignContent(`${this._Mount} .libim-savebar`, this._saveBarHTML());
	}

	_saveBarHTML()
	{
		const tmpCount = this.pendingCount();
		const tmpDisabled = (tmpCount === 0 || this._saving || !this._canEdit()) ? 'disabled' : '';
		const tmpLabel = this._saving ? 'Saving…' : (tmpCount > 0 ? `Save ${tmpCount} change${tmpCount === 1 ? '' : 's'}` : 'No changes');
		return /*html*/`
			<span class="libim-pending">${tmpCount > 0 ? `${tmpCount} unsaved change${tmpCount === 1 ? '' : 's'}` : 'All mappings saved'}</span>
			<button class="libim-btn libim-btn-primary" ${tmpDisabled} onclick="_Pict.providers.LineItemBidItemMap.saveAsync()">${tmpLabel}</button>`;
	}

	_nativeOptionsHTML(pLineItem)
	{
		const tmpSelected = this._currentValue(pLineItem);
		return this._pickerOptions(pLineItem)
			.map((pOption) => `<option value="${pOption.Value}"${pOption.Value === tmpSelected ? ' selected' : ''}>${_escape(pOption.Text)}</option>`)
			.join('');
	}

	_render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !this._Mount) { return; }
		if (tmpCA.getElement(this._Mount).length === 0) { return; }

		if (this._loading)
		{
			tmpCA.assignContent(this._Mount, /*html*/`<div class="libim-empty">Loading line items…</div>`);
			return;
		}
		if (this._lineItems.length === 0)
		{
			tmpCA.assignContent(this._Mount, /*html*/`<div class="libim-empty">This project has no line items.</div>`);
			return;
		}

		const tmpReadOnly = !this._canEdit();
		const tmpMessage = this._message ? `<div class="libim-msg ${this._messageClass}">${_escape(this._message)}</div>` : '';
		const tmpTrunc = this._truncated ? `<div class="libim-msg is-warning">Showing the first ${MAX_ROWS} line items — narrow the set on the Line Items list for the rest.</div>` : '';
		const tmpReadNote = tmpReadOnly ? `<div class="libim-msg is-warning">You can view these mappings but not change them (needs the line-item edit permission).</div>` : '';
		const tmpBigNote = (!tmpReadOnly && !this._usePickers) ? `<div class="libim-msg is-info">This project has a lot of line items — using quick dropdowns instead of the searchable picker to stay responsive.</div>` : '';

		let tmpRows = '';
		for (let i = 0; i < this._lineItems.length; i++)
		{
			const tmpLI = this._lineItems[i];
			const tmpName = _escape(tmpLI.Name || tmpLI.ItemCode || `Line Item ${tmpLI.IDLineItem}`);
			const tmpNum = _escape(tmpLI.ItemNumber || '');
			const tmpChanged = (this._pending[String(tmpLI.IDLineItem)] !== undefined) ? ' libim-changed' : '';
			const tmpControl = this._usePickers
				? `<div id="LIBIM-pick-${tmpLI.IDLineItem}" class="libim-pickmount"></div>`
				: `<select class="libim-select" ${tmpReadOnly ? 'disabled' : ''} onchange="_Pict.providers.LineItemBidItemMap.onPick(${tmpLI.IDLineItem}, this.value)">${this._nativeOptionsHTML(tmpLI)}</select>`;
			tmpRows += /*html*/`
				<tr class="libim-row${tmpChanged}" data-li="${tmpLI.IDLineItem}">
					<td class="libim-num">${tmpNum}</td>
					<td class="libim-name">${tmpName}</td>
					<td class="libim-pick">${tmpControl}</td>
				</tr>`;
		}

		tmpCA.assignContent(this._Mount, /*html*/`
			<div class="libim">
				${tmpReadNote}${tmpBigNote}${tmpTrunc}${tmpMessage}
				<div class="libim-toolbar">
					<div class="libim-count">${this._lineItems.length} line item${this._lineItems.length === 1 ? '' : 's'}</div>
					<div class="libim-savebar">${this._saveBarHTML()}</div>
				</div>
				<div class="libim-tablewrap">
					<table class="libim-table">
						<thead><tr><th>Item #</th><th>Line Item</th><th>Bid Item (this line item's project)</th></tr></thead>
						<tbody>${tmpRows}</tbody>
					</table>
				</div>
			</div>`);

		if (this._usePickers && !tmpReadOnly) { this._mountRowPickers(); }
	}

	_mountRowPickers()
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const tmpStore = this.pict.AppData.LineItemBidItemMap.RowValues;

		for (let i = 0; i < this._lineItems.length; i++)
		{
			const tmpLI = this._lineItems[i];
			const tmpID = tmpLI.IDLineItem;
			const tmpDest = `#LIBIM-pick-${tmpID}`;
			if (tmpCA.getElement(tmpDest).length === 0) { continue; }

			const tmpValue = this._currentValue(tmpLI);
			tmpStore[tmpID] = tmpValue;

			const tmpInstance = tmpPicker.createPicker(`LIBIM-Pick-${tmpID}`,
				{
					DestinationAddress: tmpDest,
					ValueAddress: `AppData.LineItemBidItemMap.RowValues.${tmpID}`,
					Searchable: true,
					Placeholder: 'Search bid items…',
					Options: this._pickerOptions(tmpLI),
					OnChange: ((pRowID) => (pNewValue) => this.onPick(pRowID, pNewValue))(tmpID),
				});
			if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
			if (tmpInstance && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(tmpValue); }
		}
	}
}

function _escape(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const _CSS = /*css*/`
	.libim { font-size: 0.9rem; }
	.libim-empty { padding: 1.2rem; color: var(--theme-color-text-muted,#6b7686); text-align:center; }
	.libim-toolbar { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin:0 0 0.7rem; flex-wrap:wrap; }
	.libim-count { font-size:0.85rem; color:var(--theme-color-text-muted,#6b7686); }
	.libim-savebar { display:flex; align-items:center; gap:0.7rem; }
	.libim-pending { font-size:0.82rem; color:var(--theme-color-text-secondary,#45505f); }
	.libim-btn { font-size:0.85rem; font-weight:600; padding:0.42rem 0.95rem; border-radius:7px; border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-panel,#fff); color:var(--theme-color-text-primary,#1f2733); cursor:pointer; }
	.libim-btn-primary { color:#fff; background:var(--theme-color-primary,#2f6fed); border-color:var(--theme-color-primary,#2f6fed); }
	.libim-btn[disabled] { opacity:0.5; cursor:default; }
	.libim-msg { font-size:0.84rem; border-radius:6px; padding:0.45rem 0.65rem; margin:0 0 0.6rem; }
	.libim-msg.is-info { color:#1f5673; background:var(--theme-color-status-info-bg,#e5f2f8); }
	.libim-msg.is-success { color:#276749; background:var(--theme-color-status-success-bg,#e6f4ea); }
	.libim-msg.is-warning { color:#8a6d1b; background:var(--theme-color-status-warning-bg,#fbf3d9); }
	.libim-msg.is-danger { color:#a12d22; background:var(--theme-color-status-danger-bg,#fbe6e3); }
	.libim-tablewrap { overflow-x:auto; border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:8px; }
	.libim-table { width:100%; border-collapse:separate; border-spacing:0; }
	.libim-table th { text-align:left; font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); background:var(--theme-color-background-tertiary,#f4f6f9); padding:0.5rem 0.85rem; border-bottom:1px solid var(--theme-color-border-light,#e8ebf0); }
	.libim-table td { padding:0.45rem 0.85rem; border-bottom:1px solid var(--theme-color-border-light,#eef1f5); vertical-align:top; }
	.libim-table tbody tr:last-child td { border-bottom:none; }
	.libim-row.libim-changed td { background:var(--theme-color-status-warning-bg,#fbf3d9); }
	.libim-num { white-space:nowrap; color:var(--theme-color-text-muted,#6b7686); font-variant-numeric:tabular-nums; padding-top:0.65rem; }
	.libim-name { color:var(--theme-color-text-primary,#1f2733); padding-top:0.6rem; }
	.libim-pick { min-width:18rem; }
	.libim-pickmount { min-width:16rem; max-width:34rem; }
	.libim-select { font-size:0.86rem; padding:0.32rem 0.5rem; border:1px solid var(--theme-color-border-medium,#d5dbe3); border-radius:6px; background:var(--theme-color-background-input,#fff); color:var(--theme-color-text-primary,#1f2733); min-width:16rem; max-width:34rem; }
	.libim-select[disabled] { opacity:0.7; }
`;

module.exports = LineItemBidItemMapProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'LineItemBidItemMap',
	AutoInitialize: true,
	AutoInitializeOrdinal: 222,
};
